import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, NgModule, NgZone, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ColorHelper, ScaleType } from '@swimlane/ngx-charts';



type StackedDatum = {
    name: string; // category (x-axis)
    series: { name: string; value: number; extra?: any }[]; // stacks
};


@Component({
    selector: 'eic-asset-analysis-chart',
    template: `
<div #chartWrap class="chart-wrap" (mousemove)="onMouseMove($event)" (mouseleave)="hideTooltip()">

    <div class="title h3">Number of Assets</div>
    <div class="subtitle h4">Analysed by Asset Type and Region</div>

    <div class="legend-wrap" *ngIf="legendPosition === 'top'">
    <ngx-charts-legend
    class="chart-legend"
        [title]="'Asset Type'"
        [data]="legendDomain"
        [colors]="legendColors"
        [horizontal]="true"
        (labelActivate)="onLegendLabelActivate($event)"
        (labelDeactivate)="onLegendLabelDeactivate($event)"
        (legendLabelClick)="onLegendLabelClick($event)">
    </ngx-charts-legend>
    </div>

    <ngx-charts-bar-vertical-stacked
        [view] = "view"     
        [scheme]="colorScheme"
        [results]="assetData"
        [gradient]="gradient"
        [xAxis]="showXAxis"
        [yAxis]="showYAxis"
        [legend]="showLegend"
        [showXAxisLabel]="showXAxisLabel"
        [showYAxisLabel]="showYAxisLabel"
        [xAxisLabel]="xAxisLabel"
        [yAxisLabel]="yAxisLabel"
        [animations]="animations"
        [tooltipDisabled]="true"
        (select)="onSelect($event)"
        (activate)="onActivate($event.value)"
        (deactivate)="onDeactivate($event.value)">
    </ngx-charts-bar-vertical-stacked>



    <div class="legend-wrap" *ngIf="legendPosition === 'bottom'">
    <ngx-charts-legend
    class="chart-legend"
        [title]="'Asset Type'"
        [data]="legendDomain"
        [colors]="legendColors"
        [horizontal]="true"
        (labelActivate)="onLegendLabelActivate($event)"
        (labelDeactivate)="onLegendLabelDeactivate($event)"
        (legendLabelClick)="onLegendLabelClick($event)">
    </ngx-charts-legend>
    </div>



  <!-- Your tooltip -->
  <div
    class="my-tooltip"
    *ngIf="tooltip.visible"
    [style.left.px]="tooltip.x"
    [style.top.px]="tooltip.y"
    role="tooltip"
    aria-live="polite">


  <!-- <pre>{{ tooltip.item | json }}</pre> -->

    <div *ngIf="tooltip.item?.series" class="title">{{ tooltip.item?.series }}</div>
    <div *ngIf="tooltip.item?.name" class="series">Asset Type: {{ tooltip.item?.name }}</div>
    <div *ngIf="tooltip.item?.value" class="value">Number of assets: {{ tooltip.item?.value | number }}</div>
    <!-- add any custom fields you attached in data.extra -->
    <!-- <div class="meta" *ngIf="tooltip.item?.extra?.note">{{ tooltip.item.extra.note }}</div> -->
  </div>

    <!-- Legend tooltip -->
  <div
    class="my-tooltip"
    *ngIf="legendTooltip.visible"
    [style.left.px]="legendTooltip.x"
    [style.top.px]="legendTooltip.y"
    role="tooltip"
    aria-live="polite">

    <div>{{ legendTooltip.displayText }}</div>
  </div>

</div>
  `,
    styles: [
        `
    :host {
        display: block;
        width: 100%;
        max-width: 100%;
    }

    .chart-wrap {
        position: relative;
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
        background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
        border-radius: 8px;
        padding: 8px;
    }

    .title,
    .subtitle {
        text-align: center;
    }

    .title {
        font-size: 20px;
        line-height: 28px;
        font-weight: 500;
        margin: 0;
    }

    .subtitle {
        font-size: 14px;
        line-height: 20px;
        margin: 0 0 10px;
        color: var(--mat-sys-on-surface-variant, var(--sys-on-surface-variant));
    }

    .my-tooltip {
        position: absolute;
        pointer-events: none; /* don’t “steal” hover from the chart */
        transform: translate(-50%, -100%);
        background: #1f2937;
        color: #fff;
        padding: .5rem .75rem;
        border-radius: .375rem;
        font-size: 12px;
        line-height: 1.2;
        box-shadow: 0 6px 18px rgba(0,0,0,.25);
        white-space: nowrap;
    }
    .my-tooltip .title { font-weight: 600; margin-bottom: 2px; }
    .my-tooltip .series { opacity: .85; margin-bottom: 2px; }
    .my-tooltip .meta { opacity: .7; font-style: italic; }


    ::ng-deep .horizontal-legend {
        display: flex;
        flex-wrap: wrap;
    }

    ::ng-deep .chart-legend {
        background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
        color: var(--mat-sys-on-surface, var(--sys-on-surface));
    }

    .legend-wrap {
        background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
        border-radius: 6px;
        padding: 4px 6px;
    }

    ::ng-deep .ngx-charts-outer,
    ::ng-deep .ngx-charts,
    ::ng-deep .ngx-charts .chart {
        background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
    }

    ::ng-deep .chart-legend .legend-labels.horizontal-legend {
        row-gap: 0;
        column-gap: 12px;
    }

    ::ng-deep .chart-legend .legend-label {
        margin-bottom: 0 !important;
        line-height: 1;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }

    ::ng-deep .chart-legend .legend-label-text,
    ::ng-deep .chart-legend .legend-label {
        white-space: nowrap !important;
        overflow: visible !important;
        text-overflow: clip !important;
        max-width: none !important;
    }

    ::ng-deep .chart-legend .legend-title,
    ::ng-deep .chart-legend .legend-title-text,
    ::ng-deep .chart-legend .legend-label,
    ::ng-deep .chart-legend .legend-label-text,
    ::ng-deep .chart-legend .legend-label:hover,
    ::ng-deep .chart-legend .legend-label:focus,
    ::ng-deep .chart-legend .legend-label:active {
        color: var(--mat-sys-on-surface, var(--sys-on-surface)) !important;
        fill: var(--mat-sys-on-surface, var(--sys-on-surface)) !important;
    }

    ::ng-deep .chart-legend .legend-label-color {
        width: 12px !important;
        height: 12px !important;
        min-width: 12px;
        min-height: 12px;
        border-radius: 50% !important;
    }
`
    ],

    changeDetection: ChangeDetectionStrategy.Default

})
export class AssetAnalysisChartComponent implements OnInit, AfterViewInit, OnDestroy {
    view: any[] = [700, 400];
    @Input() set legendPosition(value: string) {
        this._legendPosition = value === 'top' ? 'top' : 'bottom';
    }

    get legendPosition(): 'top' | 'bottom' {
        return this._legendPosition;
    }

    @ViewChild('chartWrap', { static: true }) chartWrapRef!: ElementRef<HTMLElement>;

    private resizeObserver?: ResizeObserver;
    private resizeAnimationFrameId: number | null = null;
    private readonly chartHeight = 400;
    private _legendPosition: 'top' | 'bottom' = 'bottom';

    // options
    showXAxis: boolean = true;
    showYAxis: boolean = true;
    gradient: boolean = false;
    showLegend: boolean = false;
    showXAxisLabel: boolean = true;
    xAxisLabel: string = 'Region';
    showYAxisLabel: boolean = true;
    yAxisLabel: string = 'Number of Assets';
    animations: boolean = true;



    colorScheme = {
        name: 'assetMapScheme',
        selectable: true,
        group: ScaleType.Ordinal,
        domain: [
            "#1F77B4",
            "#FF7F0E",
            "#2CA02C",
            "#D62728",
            "#9467BD",
            "#8C564B",
            "#E377C2",
            "#7F7F7F",
            "#BCBD22",
            "#17BECF",
            "#F4A261",
            "#264653",
            "#E9C46A",
            "#2A9D8F",
            "#E76F51",
            "#6A4C93",
            "#FFB703"
        ]

    };


    legendDomain: string[] = [];
    legendColors!: ColorHelper;

    constructor(private ngZone: NgZone) { }


    ngOnInit() {
        this.assetData = [...this.assetData];

        this.legendDomain = this.getSeriesNames(this.assetData);             // ['Q1','Q2','Q3']
        this.legendColors = new ColorHelper(
            this.colorScheme,                                              // scheme
            ScaleType.Ordinal,                                             // scale type
            this.legendDomain,                                             // domain (labels)
            this.colorScheme
        );


    }

    ngAfterViewInit() {
        this.updateViewFromContainer();

        this.ngZone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver((entries) => {
                const entry = entries[0];
                const width = Math.floor(entry?.contentRect?.width ?? 0);
                if (width > 0) {
                    this.ngZone.run(() => {
                        this.setViewWidth(width);
                    });
                }
            });

            this.resizeObserver.observe(this.chartWrapRef.nativeElement);
        });

        this.resizeAnimationFrameId = window.requestAnimationFrame(() => {
            this.updateViewFromContainer();
        });
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();

        if (this.resizeAnimationFrameId !== null) {
            window.cancelAnimationFrame(this.resizeAnimationFrameId);
            this.resizeAnimationFrameId = null;
        }
    }

    @HostListener('window:resize')
    onWindowResize() {
        this.updateViewFromContainer();
    }

    private updateViewFromContainer() {
        const parent = this.chartWrapRef?.nativeElement?.parentElement;
        const width = Math.floor(parent?.getBoundingClientRect().width ?? this.chartWrapRef?.nativeElement?.getBoundingClientRect().width ?? 0);
        if (width > 0) {
            this.setViewWidth(width);
        }
    }

    private setViewWidth(width: number) {
        if (this.view[0] === width && this.view[1] === this.chartHeight) {
            return;
        }

        this.view = [width, this.chartHeight];
    }


    private getSeriesNames(data: any[]): string[] {
        const set = new Set<string>();
        data.forEach(g => g.series?.forEach((s: any) => set.add(s.name)));
        return Array.from(set);
    }



    //-- Chart Tooltip ----------------------------------------------
    tooltip = { visible: false, x: 0, y: 0, item: null as null | any };
    private activeItem: any = null;

    onActivate(item: any) {
        // item typically contains { name, value, series, ... }
        this.activeItem = item;
        this.tooltip.item = item;
        this.tooltip.visible = true;
    }

    onDeactivate(item: any) {
        // hide only if we're deactivating the currently active bar segment
        if (this.activeItem &&
            item?.name === this.activeItem.name &&
            item?.series === this.activeItem.series) {
            this.tooltip.visible = false;
            this.activeItem = null;
            this.tooltip.item = null;
        }
    }

    onSelect(item: any) {
        // optional: handle clicks on a segment
        console.log('Selected', item);
    }

    hideTooltip() {
        this.tooltip.visible = false;
        this.activeItem = null;
        this.tooltip.item = null;
    }
    //---------------------------------------------------------------
    onMouseMove(evt: MouseEvent) {
        // if (!this.tooltip.visible) return;
        // const wrap = (evt.currentTarget as HTMLElement).getBoundingClientRect();
        // // offset so the tooltip doesn't cover the cursor
        // this.tooltip.x = evt.clientX - wrap.left + 12;
        // this.tooltip.y = evt.clientY - wrap.top - 12;

        if (this.tooltip.visible) {
            const wrap = (evt.currentTarget as HTMLElement).getBoundingClientRect();
            // offset so the tooltip doesn't cover the cursor
            this.tooltip.x = evt.clientX - wrap.left + 12;
            this.tooltip.y = evt.clientY - wrap.top - 12;
        }
        if (this.legendTooltip.visible) {
            const wrap = (evt.currentTarget as HTMLElement).getBoundingClientRect();
            // offset so the tooltip doesn't cover the cursor
            this.legendTooltip.x = evt.clientX - wrap.left + 12;
            this.legendTooltip.y = evt.clientY - wrap.top - 12;
        }

    }
    //-- LegendTooltip ----------------------------------------------
    legendTooltip = { visible: false, x: 0, y: 0, item: null as null | any, displayText: "" };
    private activeLegendItem: any = null;

    onLegendLabelActivate(item: any) {
        this.activeLegendItem = item;
        this.legendTooltip.item = item;

        // Compute the total caount to be shown on the tooltip
        // and format it into a display string
        const globalTypeCount = this.sumByType(this.assetData, item.name);

        this.legendTooltip.displayText = `${item.name}: ${globalTypeCount} global assets`

        this.legendTooltip.visible = true;
    }

    onLegendLabelDeactivate(item: any) {
        // hide only if we're deactivating the currently active bar segment
        // if (this.activeItem &&
        //     item?.name === this.activeItem.name &&
        //     item?.series === this.activeItem.series) {
        this.legendTooltip.visible = false;
        this.activeLegendItem = null;
        this.legendTooltip.item = null;
        // }
    }

    onLegendLabelClick($event: any) {

    }

    hideLegendTooltip() {
        this.legendTooltip.visible = false;
        this.activeLegendItem = null;
        this.legendTooltip.item = null;
    }



    sumByType(data, typeName) {
        if (!Array.isArray(data) || !typeName) return 0;
        const target = typeName.trim().toLowerCase();

        return data.reduce((total, region) => {
            const item = (region?.series || []).find(
                s => s?.name && s.name.trim().toLowerCase() === target
            );
            const v = Number(item?.value);
            return total + (Number.isFinite(v) ? v : 0);
        }, 0);
    }


    //---------------------------------------------------------------
    assetData = [
        {
            "name": "Africa",
            "series": [
                { "name": "Biofuel Plant", "value": 5 },
                { "name": "Biomass Fired Plant", "value": 16 },
                { "name": "Carbon Capture", "value": 2 },
                { "name": "Energy from Waste Plant", "value": 3 },
                { "name": "Energy Storage", "value": 16 },
                { "name": "Field", "value": 853 },
                { "name": "Hydroelectric Plant", "value": 181 },
                { "name": "Hydrogen", "value": 1 },
                { "name": "LNG", "value": 16 },
                { "name": "Nuclear Power Plant", "value": 4 },
                { "name": "Petrochemical Complex", "value": 90 },
                { "name": "Pipeline", "value": 231 },
                { "name": "Power Plant", "value": 444 },
                { "name": "Refinery", "value": 62 },
                { "name": "Solar Farm", "value": 156 },
                { "name": "Terminal", "value": 108 },
                { "name": "Wind Farm", "value": 99 }
            ]
        },
        {
            "name": "Asia",
            "series": [
                { "name": "Biofuel Plant", "value": 50 },
                { "name": "Biomass Fired Plant", "value": 106 },
                { "name": "Carbon Capture", "value": 10 },
                { "name": "Energy from Waste Plant", "value": 107 },
                { "name": "Energy Storage", "value": 74 },
                { "name": "Field", "value": 1102 },
                { "name": "Hydroelectric Plant", "value": 864 },
                { "name": "Hydrogen", "value": 17 },
                { "name": "LNG", "value": 86 },
                { "name": "Nuclear Power Plant", "value": 39 },
                { "name": "Petrochemical Complex", "value": 432 },
                { "name": "Pipeline", "value": 313 },
                { "name": "Power Plant", "value": 815 },
                { "name": "Refinery", "value": 84 },
                { "name": "Solar Farm", "value": 560 },
                { "name": "Terminal", "value": 161 },
                { "name": "Wind Farm", "value": 391 }
            ]
        },
        {
            "name": "Australasia",
            "series": [
                { "name": "Biofuel Plant", "value": 1 },
                { "name": "Biomass Fired Plant", "value": 39 },
                { "name": "Carbon Capture", "value": 4 },
                { "name": "Energy from Waste Plant", "value": 14 },
                { "name": "Energy Storage", "value": 41 },
                { "name": "Field", "value": 468 },
                { "name": "Hydroelectric Plant", "value": 92 },
                { "name": "Hydrogen", "value": 17 },
                { "name": "LNG", "value": 19 },
                { "name": "Petrochemical Complex", "value": 15 },
                { "name": "Pipeline", "value": 43 },
                { "name": "Power Plant", "value": 157 },
                { "name": "Refinery", "value": 4 },
                { "name": "Solar Farm", "value": 113 },
                { "name": "Terminal", "value": 31 },
                { "name": "Wind Farm", "value": 126 }
            ]
        },
        {
            "name": "Europe",
            "series": [
                { "name": "Biofuel Plant", "value": 148 },
                { "name": "Biomass Fired Plant", "value": 322 },
                { "name": "Carbon Capture", "value": 25 },
                { "name": "Energy from Waste Plant", "value": 378 },
                { "name": "Energy Storage", "value": 336 },
                { "name": "Field", "value": 1143 },
                { "name": "Hydroelectric Plant", "value": 1490 },
                { "name": "Hydrogen", "value": 110 },
                { "name": "LNG", "value": 69 },
                { "name": "Nuclear Power Plant", "value": 134 },
                { "name": "Petrochemical Complex", "value": 402 },
                { "name": "Pipeline", "value": 656 },
                { "name": "Power Plant", "value": 1009 },
                { "name": "Refinery", "value": 154 },
                { "name": "Solar Farm", "value": 1606 },
                { "name": "Terminal", "value": 578 },
                { "name": "Wind Farm", "value": 3031 }
            ]
        },
        {
            "name": "Indian sub-continent",
            "series": [
                { "name": "Biofuel Plant", "value": 10 },
                { "name": "Biomass Fired Plant", "value": 549 },
                { "name": "Carbon Capture", "value": 2 },
                { "name": "Energy from Waste Plant", "value": 14 },
                { "name": "Energy Storage", "value": 13 },
                { "name": "Field", "value": 264 },
                { "name": "Hydroelectric Plant", "value": 416 },
                { "name": "Hydrogen", "value": 17 },
                { "name": "LNG", "value": 17 },
                { "name": "Nuclear Power Plant", "value": 25 },
                { "name": "Petrochemical Complex", "value": 76 },
                { "name": "Pipeline", "value": 191 },
                { "name": "Power Plant", "value": 595 },
                { "name": "Refinery", "value": 33 },
                { "name": "Solar Farm", "value": 539 },
                { "name": "Terminal", "value": 162 },
                { "name": "Wind Farm", "value": 235 }
            ]
        },
        {
            "name": "Middle East",
            "series": [
                { "name": "Biofuel Plant", "value": 1 },
                { "name": "Carbon Capture", "value": 7 },
                { "name": "Energy from Waste Plant", "value": 3 },
                { "name": "Energy Storage", "value": 5 },
                { "name": "Field", "value": 881 },
                { "name": "Hydroelectric Plant", "value": 46 },
                { "name": "Hydrogen", "value": 6 },
                { "name": "LNG", "value": 19 },
                { "name": "Nuclear Power Plant", "value": 4 },
                { "name": "Petrochemical Complex", "value": 238 },
                { "name": "Pipeline", "value": 483 },
                { "name": "Power Plant", "value": 460 },
                { "name": "Refinery", "value": 78 },
                { "name": "Solar Farm", "value": 63 },
                { "name": "Terminal", "value": 159 },
                { "name": "Wind Farm", "value": 30 }
            ]
        },
        {
            "name": "North America",
            "series": [
                { "name": "Biofuel Plant", "value": 502 },
                { "name": "Biomass Fired Plant", "value": 329 },
                { "name": "Carbon Capture", "value": 51 },
                { "name": "Energy from Waste Plant", "value": 394 },
                { "name": "Energy Storage", "value": 242 },
                { "name": "Field", "value": 875 },
                { "name": "Hydroelectric Plant", "value": 733 },
                { "name": "Hydrogen", "value": 77 },
                { "name": "LNG", "value": 60 },
                { "name": "Nuclear Power Plant", "value": 118 },
                { "name": "Petrochemical Complex", "value": 641 },
                { "name": "Pipeline", "value": 1342 },
                { "name": "Power Plant", "value": 2185 },
                { "name": "Refinery", "value": 166 },
                { "name": "Solar Farm", "value": 1068 },
                { "name": "Terminal", "value": 4922 },
                { "name": "Wind Farm", "value": 1406 }
            ]
        },
        {
            "name": "South America",
            "series": [
                { "name": "Biofuel Plant", "value": 119 },
                { "name": "Biomass Fired Plant", "value": 445 },
                { "name": "Carbon Capture", "value": 3 },
                { "name": "Energy from Waste Plant", "value": 11 },
                { "name": "Energy Storage", "value": 20 },
                { "name": "Field", "value": 1087 },
                { "name": "Hydroelectric Plant", "value": 718 },
                { "name": "Hydrogen", "value": 19 },
                { "name": "LNG", "value": 22 },
                { "name": "Nuclear Power Plant", "value": 5 },
                { "name": "Petrochemical Complex", "value": 139 },
                { "name": "Pipeline", "value": 401 },
                { "name": "Power Plant", "value": 552 },
                { "name": "Refinery", "value": 58 },
                { "name": "Solar Farm", "value": 517 },
                { "name": "Terminal", "value": 462 },
                { "name": "Wind Farm", "value": 1125 }
            ]
        }
    ]


}
